'use client'
import {SRCImage} from 'react-datocms'
import DisclaimerStructuredText from '~/cms/CMSPage/components/Disclaimers/DisclaimerStructuredText'
import {type HeroFragment} from '~/cms/CMSPage/components/HeroSection/_generated/fragments.graphql'
import Asset from '~/cms/components/Asset'
import {getButtonLinkProps} from '~/cms/components/ButtonLinkComponent'
import {DatoThemeSelector} from '~/cms/components/DatoThemeSelector'
import {DatoThemeSwitcher} from '~/cms/components/DatoThemeSwitcher'
import {SingleLineStructuredText} from '~/cms/components/SingleLineStructuredText'
import {getEmailCaptureDestinationProps} from '~/cms/helpers/getEmailCaptureProps/getEmailCaptureDestinationProps'
import {
  HgHero,
  type HgHeroBaseProps,
  type HgHeroCTAProps,
} from '~/design-system/hg/modules'
import {safeUnreachableCase} from '~/utils/unreachableCase'

type HeroSectionProps = {
  data: HeroFragment
}

function getCTATypeProps(data: HeroFragment): HgHeroCTAProps {
  if (data.ctaType === 'email_capture' && data.emailCapture) {
    return {
      contentType: 'emailCapture',
      emailCaptureProps: getEmailCaptureDestinationProps(data.emailCapture),
    }
  }

  return {
    contentType: 'buttonGroup',
    primaryButtonProps: getButtonLinkProps(data.primaryButton),
    secondaryButtonProps: getButtonLinkProps(data.secondaryButton),
  }
}

export default function HeroSection({data}: HeroSectionProps) {
  const {variant, disclaimer, title, subhead, theme} = data

  const defaultProps: HgHeroBaseProps = {
    ...getCTATypeProps(data),
    headline: <SingleLineStructuredText data={title} />,
    subhead: <SingleLineStructuredText data={subhead} />,
    disclaimer: disclaimer && <DisclaimerStructuredText data={disclaimer.text} />,
  }

  if (!variant) {
    return (
      <DatoThemeSwitcher theme={theme}>
        <section data-section="hero">
          <HgHero {...defaultProps} type="headingOnly" />
        </section>
      </DatoThemeSwitcher>
    )
  }

  switch (variant.__typename) {
    case 'CinematicHeroComponentRecord': {
      return (
        <DatoThemeSwitcher theme={theme} applyGlobally={false}>
          <div>
            <DatoThemeSelector theme={theme}>
              <section data-section="hero">
                <HgHero
                  type="cinematic"
                  // TODO: improve implementation of aspect ratio across screens
                  backgroundImage={
                    <>
                      {/* eslint-disable-next-line jsx-a11y/alt-text -- alt/title is handled by datocms */}
                      <SRCImage
                        className="hidden object-cover 3xl:block"
                        style={{height: '100%'}}
                        data={
                          variant.heroSixteenNineBackgroundImage.image
                            .responsiveImage
                        }
                        priority
                      />
                      {/* eslint-disable-next-line jsx-a11y/alt-text -- alt/title is handled by datocms */}
                      <SRCImage
                        className="hidden object-cover lg:block 3xl:hidden"
                        style={{height: '100%'}}
                        data={
                          variant.heroThreeTwoBackgroundImage.image.responsiveImage
                        }
                        priority
                      />
                      {/* eslint-disable-next-line jsx-a11y/alt-text -- alt/title is handled by datocms */}
                      <SRCImage
                        className="hidden object-cover sm:block lg:hidden"
                        style={{height: '100%'}}
                        data={
                          variant.heroFourFiveBackgroundImage.image.responsiveImage
                        }
                        priority
                      />
                    </>
                  }
                  maskImage={
                    <>
                      {/* eslint-disable-next-line jsx-a11y/alt-text -- alt/title is handled by datocms */}
                      <SRCImage
                        className="hidden object-cover 3xl:block"
                        style={{height: '100%'}}
                        data={variant.heroSixteenNineMaskImage.image.responsiveImage}
                        priority
                      />
                      {/* eslint-disable-next-line jsx-a11y/alt-text -- alt/title is handled by datocms */}
                      <SRCImage
                        className="hidden object-cover lg:block 3xl:hidden"
                        style={{height: '100%'}}
                        data={variant.heroThreeTwoMaskImage.image.responsiveImage}
                        priority
                      />
                      {/* eslint-disable-next-line jsx-a11y/alt-text -- alt/title is handled by datocms */}
                      <SRCImage
                        className="hidden object-cover sm:block lg:hidden"
                        style={{height: '100%'}}
                        data={variant.heroFourFiveMaskImage.image.responsiveImage}
                        priority
                      />
                    </>
                  }
                  mobileMaskImage={
                    // eslint-disable-next-line jsx-a11y/alt-text -- alt/title is handled by datocms
                    <SRCImage
                      className="object-cover"
                      style={{height: '100%'}}
                      data={variant.mobileMaskImage.image.responsiveImage}
                      priority
                    />
                  }
                  {...defaultProps}
                />
              </section>
            </DatoThemeSelector>
          </div>
        </DatoThemeSwitcher>
      )
    }
    case 'DefaultHeroComponentRecord':
      return (
        <DatoThemeSwitcher theme={theme}>
          <section data-section="hero">
            <HgHero
              {...defaultProps}
              type="default"
              assetTwoOneRatio={
                <Asset
                  data={variant.defaultHeroModuleTwoOneAsset}
                  aspectRatio="2:1"
                />
              }
              assetFourFiveRatio={
                <Asset
                  data={variant.defaultHeroModuleFourFiveAsset}
                  aspectRatio="4:5"
                />
              }
            />
          </section>
        </DatoThemeSwitcher>
      )
    default:
      return safeUnreachableCase(variant, null)
  }
}
